<template>
  <eg-b-dropdown
    ref="dropdown"
    class="metrics-multi-filter"
    no-flip
    toggle-class="metrics-multi-filter__toggle"
    menu-class="metrics-multi-filter__menu"
  >
    <template #button-content>
      {{ displayText }}
    </template>

    <div class="metrics-multi-filter__form">
      <label class="metrics-multi-filter__item metrics-multi-filter__item--all">
        <input
          ref="allCheckbox"
          type="checkbox"
          class="metrics-multi-filter__checkbox"
          :checked="isAllSelected"
          @change="onToggleAll"
        />
        <span>{{ allLabel }}</span>
      </label>

      <div class="metrics-multi-filter__divider" />

      <label
        v-for="item in items"
        :key="item.id"
        class="metrics-multi-filter__item"
      >
        <input
          type="checkbox"
          class="metrics-multi-filter__checkbox"
          :checked="isSelected(item.id)"
          @change="onToggleItem(item.id, $event)"
        />
        <span class="metrics-multi-filter__item-text">{{ item.name }}</span>
      </label>
    </div>
  </eg-b-dropdown>
</template>

<script>
import dropdownHideOnLeaveMixin from "@/mixins/dropdownHideOnLeaveMixin";

export default {
  name: "MetricsMultiFilter",

  mixins: [dropdownHideOnLeaveMixin],

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    allLabel: {
      type: String,
      default: "",
    },
    // null = all selected (no filter)
    // [] = none selected
    // [ids...] = filter to these IDs
    value: {
      default: null,
    },
  },

  computed: {
    isAllSelected() {
      return this.value === null;
    },

    isPartiallySelected() {
      return (
        this.value !== null &&
        this.value.length > 0 &&
        this.value.length < this.items.length
      );
    },

    displayText() {
      if (this.value === null) return this.allLabel;
      if (this.value.length === 0) return this.$t("metrics.noneSelected");
      if (this.value.length === 1) {
        const found = this.items.find((a) => a.id === this.value[0]);
        return found
          ? found.name
          : this.$t("metrics.countSelected", { count: 1 });
      }
      return this.$t("metrics.countSelected", { count: this.value.length });
    },
  },

  watch: {
    isPartiallySelected: {
      immediate: true,
      handler(val) {
        this.$nextTick(() => {
          if (this.$refs.allCheckbox) {
            this.$refs.allCheckbox.indeterminate = val;
          }
        });
      },
    },
  },

  mounted() {
    this._outsideClickHandler = (e) => {
      if (!this.$el.contains(e.target)) {
        this.$refs.dropdown.hide();
      }
    };
    document.addEventListener("click", this._outsideClickHandler, true);
  },

  beforeDestroy() {
    document.removeEventListener("click", this._outsideClickHandler, true);
  },

  methods: {
    isSelected(id) {
      if (this.value === null) return true;
      if (this.value.length === 0) return false;
      return this.value.includes(id);
    },

    onToggleAll() {
      if (this.isAllSelected) {
        if (!this.items.length) return;
        this.$emit("input", [this.items[0].id]);
      } else {
        this.$emit("input", null);
      }
    },

    onToggleItem(id, event) {
      const checked = event.target.checked;
      let current =
        this.value === null ? this.items.map((a) => a.id) : [...this.value];

      if (checked) {
        if (!current.includes(id)) current.push(id);
      } else {
        current = current.filter((i) => i !== id);
      }

      if (current.length === 0) {
        event.target.checked = true;
        return;
      }

      const next = current.length === this.items.length ? null : current;
      this.$emit("input", next);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "metrics_card";

.metrics-multi-filter {
  min-width: 180px;

  ::v-deep .metrics-multi-filter__toggle {
    @include metrics-dropdown-btn;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px;
    background: var(--color-surface-neutral-background, #fff);
    &:focus,
    &:active {
      box-shadow: none;
      outline: none;
    }

    &::after {
      margin-left: 8px;
      flex-shrink: 0;
    }
  }

  ::v-deep .metrics-multi-filter__toggle[aria-expanded="true"] {
    border-color: var(--color-stroke-primary-default, #8a3ffc) !important;
    background: var(--color-surface-neutral-hover, #f6f2ff) !important;
  }

  ::v-deep .metrics-multi-filter__menu {
    border-radius: var(--radius-rd-300, 8px);
    border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    padding: 8px 0;
    min-width: 220px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    overscroll-behavior: none;
  }

  &__form {
    padding: 4px 0;
    display: flex;
    flex-direction: column;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 16px;
    margin: 0;
    cursor: pointer;
    color: var(--color-text-neutral-on-white);
    font-size: var(--typography-font-size-fs-300, 14px);
    font-weight: var(--typography-font-weight-fw-400, 400);

    &:hover {
      background: var(--color-surface-neutral-container-1, #f7f9fa);
    }

    &--all {
      font-weight: var(--typography-font-weight-fw-600, 600);
      color: var(--color-text-neutral-secondary, #566370);
    }
  }

  &__checkbox {
    appearance: none;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border: 1px solid var(--color-surface-neutral-container-5, #c5d0d8);
    border-radius: 4px;
    background-color: transparent;
    position: relative;
    cursor: pointer;
    transition: all 0.2s ease;

    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 13px;
      height: 13px;
      transform: translate(-50%, -50%) scale(0);
      transition: transform 0.2s ease;
    }

    &:checked {
      background: var(--color-surface-primary-default, #8a3ffc);
      border-color: var(--color-surface-primary-default, #8a3ffc);

      &::before {
        transform: translate(-50%, -50%) scale(1);
        background-image: url("data:image/svg+xml,%3Csvg%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%20fill%3D%22none%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20clip-path%3D%22url(%23clip0_40010750_11460)%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M10.2652%202.73483C10.4116%202.88128%2010.4116%203.11872%2010.2652%203.26517L4.76517%208.76516C4.61872%208.91161%204.38128%208.91161%204.23483%208.76516L1.73483%206.26517C1.58839%206.11872%201.58839%205.88128%201.73483%205.73483C1.88128%205.58839%202.11872%205.58839%202.26517%205.73483L4.5%207.96967L9.73483%202.73483C9.88128%202.58839%2010.1187%202.58839%2010.2652%202.73483Z%22%20fill%3D%22white%22/%3E%3C/g%3E%3Cdefs%3E%3CclipPath%20id%3D%22clip0_40010750_11460%22%3E%3Crect%20width%3D%2212%22%20height%3D%2212%22%20fill%3D%22white%22/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
        background-size: contain;
      }
    }

    &:indeterminate {
      background: var(--color-surface-primary-default, #8a3ffc);
      border-color: var(--color-stroke-primary-default, #8a3ffc);

      &::before {
        content: "";
        width: 8px;
        height: 1px;
        border-radius: 0;
        transform: translate(-50%, -50%) scale(1);
        background-color: #fff;
        background-image: none;
      }
    }
  }

  &__item-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 220px;
  }

  &__divider {
    height: 1px;
    background: var(--color-stroke-neutral-default, #e5ebf0);
    margin: 4px 0;
  }
}
</style>
